import React, { useEffect, useRef, useState } from 'react'
import ReCAPTCHA from 'react-google-recaptcha'
import { useTranslation } from 'react-i18next'
import LanguageSwitcher from '../components/LanguageSwitcher'
import Logo from '../components/Logo'

function isCaptchaDisabled() {
  if (process.env.REACT_APP_DISABLE_RECAPTCHA === 'true') return true
  if (process.env.NODE_ENV === 'development') return true
  if (typeof window === 'undefined') return false
  const h = window.location.hostname
  return h === 'localhost' || h === '127.0.0.1' || h === '[::1]'
}

function AuthForm({ onLoginSuccess }) {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [captchaToken, setCaptchaToken] = useState('');
  const [csrfToken, setCsrfToken] = useState('');
  const [error, setError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const captchaRef = useRef();
  const captchaDisabled = isCaptchaDisabled();

  // 1. Получаем CSRF токен
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/csrf-token`, {
      credentials: 'include'
    })
      .then(res => res.json())
      .then(data => setCsrfToken(data.csrfToken))
      .catch(() => {});
  }, []);

  // 2. Валидация email
  const validateEmail = (email) => {
    const re = /^[\w-.]+@([\w-]+\.)+[\w-]{2,4}$/;
    return re.test(String(email).toLowerCase());
  };

  // 3. Очистка опасного текста
  const sanitize = (input) => {
    const div = document.createElement('div');
    div.innerText = input;
    return div.innerHTML;
  };

  /**
   * Сохраняет данные Ruijie сессии
   * SESSION cookie устанавливается автоматически бэкендом на общий домен
   */
  const saveRuijieSessionData = (ruijieData) => {
    if (!ruijieData || !ruijieData.connected) {
      console.log('[Ruijie] Not connected, skipping session data save');
      return;
    }

    try {
      // Сохраняем данные пользователя Ruijie в localStorage
      localStorage.setItem('ruijieSession', JSON.stringify({
        ...ruijieData.userData,
        connected: true,
        timestamp: Date.now(),
        baseUrl: ruijieData.baseUrl,
      }));

      // Сохраняем sessionStorage данные (для информации)
      // Примечание: sessionStorage специфичен для домена, 
      // поэтому эти данные доступны только на нашем домене
      // На sapa.mektep.kai.kz Ruijie OCE сам заполнит sessionStorage при первом визите
      if (ruijieData.sessionStorageData) {
        localStorage.setItem('ruijieSessionStorageData', JSON.stringify(ruijieData.sessionStorageData));
      }

      console.log('[Ruijie] ✅ Session data saved to localStorage');
      console.log('[Ruijie] 🍪 SESSION cookie was set by backend for domain sharing');

      // Уведомляем приложение
      window.dispatchEvent(new CustomEvent('ruijieSessionReady', { 
        detail: ruijieData.userData 
      }));

    } catch (err) {
      console.error('[Ruijie] Error saving session data:', err);
    }
  };

  // 4. Отправка формы
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoggingIn(true);

    if (!validateEmail(email)) {
      setIsLoggingIn(false);
      return setError(t('auth.invalidEmail'));
    }
    if (password.length < 6) {
      setIsLoggingIn(false);
      return setError(t('auth.passwordMinLength'));
    }
    if (!captchaDisabled && !captchaToken) {
      setIsLoggingIn(false);
      return setError(t('auth.captchaRequired'));
    }

    const trimmedEmail = email.trim();
    const trimmedPassword = password.trim();

    try {
      // Единый запрос на логин
      // Backend параллельно логинит и в нашу систему, и в Ruijie OCE
      // SESSION cookie для Ruijie устанавливается автоматически
      const response = await fetch(`${process.env.REACT_APP_API_URL}/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        credentials: 'include', // Важно для получения cookies!
        body: JSON.stringify({
          email: trimmedEmail,
          password: trimmedPassword,
          ...(captchaDisabled ? {} : { captcha: captchaToken }),
        })
      });

      let data;
      try {
        const text = await response.text();
        data = JSON.parse(text);
      } catch (err) {
        setIsLoggingIn(false);
        setError(t('auth.serverError'));
        return;
      }

      if (response.ok && data.accessToken) {
        // Сохраняем данные Ruijie сессии если SSO прошел успешно
        if (data.ruijie) {
          saveRuijieSessionData(data.ruijie);
          
          if (data.ruijie.connected) {
            console.log('[Auth] ✅ Dual login successful (Local + Ruijie OCE)');
          } else {
            console.log('[Auth] ⚠️ Local login OK, Ruijie SSO unavailable');
          }
        }

        setIsLoggingIn(false);
        onLoginSuccess(data.accessToken, data.role, data.canRespondToIncidents);
      } else {
        setIsLoggingIn(false);
        setError(sanitize(data.message || t('auth.invalidCredentials')));
      }
    } catch (err) {
      setIsLoggingIn(false);
      setError(t('auth.serverError'));
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-surface-base text-muted-100">
      <div className="pointer-events-none absolute inset-0 bg-brand-radial opacity-50" />
      <div className="pointer-events-none absolute inset-y-0 left-0 w-1/3 bg-[radial-gradient(circle_at_top,_rgba(64,230,255,0.18),_transparent_55%)]" />
      <div className="pointer-events-none absolute inset-y-0 right-0 w-1/2 bg-[radial-gradient(circle_at_center,_rgba(110,88,255,0.2),_transparent_60%)]" />
      
      {/* Переключатель языка в правом верхнем углу */}
      <div className="absolute top-4 right-4 sm:top-6 sm:right-6 z-20">
        <LanguageSwitcher />
      </div>
      
      <div className="relative z-10 mx-auto flex w-full max-w-[1400px] flex-col gap-8 px-4 py-8 sm:px-6 sm:py-12 md:flex-row md:items-center md:justify-between md:gap-8 lg:px-12">
        <Logo />
        <section className="w-full md:max-w-[600px] lg:max-w-[800px]">
          <span className="inline-flex items-center gap-2 rounded-full bg-brand-400/15 px-3 py-1 text-[10px] sm:text-[11px] font-semibold uppercase tracking-[0.4em] text-brand-100">
            control hub
          </span>
          <h1 className="mt-4 sm:mt-6 font-display text-3xl sm:text-4xl lg:text-5xl leading-tight text-white">
            {t('auth.systemTitle')}
          </h1>
          <p className="mt-3 sm:mt-4 text-sm sm:text-base lg:text-lg text-muted-300">
            {t('auth.systemDescription')}
          </p>
          <div className="mt-6 sm:mt-10 grid grid-cols-1 gap-3 sm:gap-4 text-sm text-muted-300 md:grid-cols-2">
            <div className="rounded-2xl sm:rounded-3xl border border-surface-border/40 bg-surface-base/60 px-3 py-2.5 sm:px-4 sm:py-3 shadow-soft">
              <span className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-muted-500">{t('auth.security')}</span>
              <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-muted-200">{t('auth.securityDescription')}</p>
            </div>
            <div className="rounded-2xl sm:rounded-3xl border border-surface-border/40 bg-surface-base/60 px-3 py-2.5 sm:px-4 sm:py-3 shadow-soft">
              <span className="text-[10px] sm:text-xs uppercase tracking-[0.3em] text-muted-500">{t('auth.access')}</span>
              <p className="mt-1.5 sm:mt-2 text-xs sm:text-sm text-muted-200">{t('auth.accessDescription')}</p>
            </div>
          </div>
        </section>

        <section className="relative w-full md:max-w-[500px] lg:max-w-[600px] xl:w-[600px] overflow-hidden rounded-2xl sm:rounded-3xl border border-surface-border/50 bg-surface-raised/85 p-6 sm:p-8 shadow-elevated backdrop-blur-xl h-fit">
          <div className="pointer-events-none absolute -top-40 -right-20 h-64 w-64 rounded-full bg-brand-400/20 blur-3xl" />
          <div className="pointer-events-none absolute inset-x-0 top-0 h-1 rounded-full bg-brand-linear" />

          <div className="relative flex flex-col gap-6">
            <header className="text-center">
              <h2 className="font-display text-2xl text-white">{t('auth.title')}</h2>
              <p className="mt-2 text-sm text-muted-400">{t('auth.subtitle')}</p>
            </header>

            {error && (
              <div className="rounded-2xl border border-critical/40 bg-critical/10 p-4 text-sm text-critical" role="alert">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} autoComplete="off" className="flex flex-col gap-3 sm:gap-4">
              <label htmlFor="email" className="flex flex-col gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-muted-200">
                {t('auth.email')}
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full rounded-xl sm:rounded-2xl bg-surface-base/70 px-3 py-2.5 sm:px-4 sm:py-3 text-sm text-white outline-none transition focus-visible:ring-2 focus-visible:ring-brand-400/70"
                  placeholder="name@example.com"
                />
              </label>

              <label htmlFor="password" className="flex flex-col gap-1.5 sm:gap-2 text-xs sm:text-sm font-semibold text-muted-200">
                {t('auth.password')}
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full rounded-xl sm:rounded-2xl bg-surface-base/70 px-3 py-2.5 sm:px-4 sm:py-3 text-sm text-white outline-none transition focus-visible:ring-2 focus-visible:ring-brand-400/70"
                  placeholder="••••••••"
                />
              </label>

              {!captchaDisabled && (
                <div className="flex justify-center">
                  <div className="rounded-xl sm:rounded-2xl bg-surface-base/60 p-1.5 sm:p-2 shadow-soft transform scale-90 sm:scale-60">
                    <ReCAPTCHA
                      sitekey="6LfNIh4rAAAAAOkdzOcENrDfDLN6r3VgS0U_83sj"
                      onChange={(token) => setCaptchaToken(token)}
                      ref={captchaRef}
                    />
                  </div>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoggingIn}
                className="inline-flex items-center justify-center gap-2 rounded-xl sm:rounded-2xl bg-brand-linear px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-semibold text-white shadow-brand transition hover:brightness-110 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400/60 disabled:opacity-70 disabled:cursor-not-allowed min-h-[44px]"
              >
                {isLoggingIn ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    {t('auth.loggingIn', 'Вход...')}
                  </>
                ) : (
                  <>
                    <span className="material-icons text-base">login</span>
                    {t('auth.login')}
                  </>
                )}
              </button>
            </form>

            <p className="text-center text-xs text-muted-500">
              {t('auth.support')}
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}

export default AuthForm;
